/*
  FETCH API
  Follows: https://github.com/matthew-andrews/isomorphic-fetch
  with the following modifications:
  Outgoing Requests and Incoming Responses are automatically logged: always on the browser and on the server in dev environments
  Options:
    - Headers `Accept` and `Content-Type` default to `application/json`
    - `method` defaults to `GET`
    - `tries`: An integer indicating how many times the request should be tried.  Defaults to 2 on the server (1 extra try), 1 on the browser (doesn't retry at all)
    - `queryParams`: Accepts an Object that will be converted into a valid queryString or a String already formatted and appended it to the uri given.

  Throws errors if the response code is not ok (4xx, 5xx), should be caught with `.catch()`. See,
  https://fetch.spec.whatwg.org/#statuses

  Response:
    - The response body will automatically be parsed for you and assigned to `body` on the returned response object.  Similar to express's body parsing middleware
    - On error responses, if the response is an object, automatically assign `url` and `method` to the error object
 */

require("isomorphic-fetch");

const merge = require("lodash/merge");
const omit = require("lodash/omit");
const isEmpty = require("lodash/isEmpty");
const get = require("lodash/get");
const isPlainObject = require("lodash/isPlainObject");
const Cookies = require("js-cookie");
const { v4: uuidv4 } = require("uuid");
const logger = require("@ten-x/logger");
const libAuth = require("@ten-x/js-lib-auth").default;

const proxyRoute = require("../apiGateway/proxyRoute");

const { isNode, canUseDom } = require("./environments");
const serializeParams = require("./serializeParams");

const { createProxyRoute } = proxyRoute;

const constructUrl = (url, params = {}) => {
  if (isEmpty(params)) {
    return url;
  }

  // If we have a string, this is skipped but we'll need to serialize if we've been given an object
  if (isPlainObject(params)) params = serializeParams(params);

  return `${url}?${params}`;
};

/**
 * We need to do this because the response is already present from `isomorphic-fetch` and we
 * can overwrite it in a node environment, but not in a browser.
 */
const setResponseBody = (response, value) => {
  Object.defineProperty(response, "body", {
    value,
    enumerable: true,
  });
};

/**
 * These functions are responsible for accepting a response from native fetch, and parsing it into
 * an appropriate format
 */
const responseParsers = {
  blob: (response) => {
    return response.blob().then((blob) => {
      setResponseBody(response, blob);

      return Promise.resolve(response);
    });
  },

  arrayBuffer: (response) => {
    return response.arrayBuffer().then((buffer) => {
      setResponseBody(response, buffer);

      return Promise.resolve(response);
    });
  },

  formData: (response) => {
    return response.formData().then((data) => {
      setResponseBody(response, data);

      return Promise.resolve(response);
    });
  },

  // do not parse the response if client needs the raw stream such as reading a file
  // the text parser attempts to convert raw data into a string, which could transform binary data incorrectly for a binary stream
  rawStream: (response) => {
    return Promise.resolve(response).then((data) => {
      setResponseBody(response, data.body);
      return Promise.resolve(response);
    });
  },

  /**
   * Even though this is called json, it also handles plain text responses. The reason we mix the
   * two is because we want to add a few sanity guards by default, like avoiding trying to
   * JSON.parse an empty response body.
   */
  json: (response) => {
    // NOTE: Should be case insensitive https://www.w3.org/Protocols/rfc2616/rfc2616-sec4.html#sec4.2
    var contentType = response.headers.get("content-type") || "";

    // Don't use .json in case response length is 0, which is very common for 204 responses
    return response
      .text()
      .then(function (text) {
        return text && text.length && contentType.includes("application/json")
          ? JSON.parse(text)
          : text;
      })
      .then(function (parsedBody) {
        setResponseBody(response, parsedBody);

        return Promise.resolve(response);
      });
  },
};

const defaultOptions = {
  method: "GET",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  credentials: "same-origin",
  queryParams: {},
  parseResponse: responseParsers.json,
  tries: canUseDom() ? 1 : 2, // Retry once on the server, none on the browser
  retryTimeout: 500, // Amount of time to wait between tries

  // The flag controls whether to try and match against a proxy route. It allows people to opt-out, even if they url
  // matches a valid proxy pattern
  useProxy: true,
};

const strictDefaultOptions = {
  method: "GET",
  // TODO REMOVE
  // NOTE: headers object needs to be defined at least as empty object because a big chunk
  // of code depends on it being defined (specially where we do options.headers['something'])
  headers: {},
  // TODO REMOVE
  credentials: "same-origin",
  // TODO REMOVE
  queryParams: {},
  // TODO REMOVE (can we?)
  parseResponse: responseParsers.json,
  // TODO REMOVE
  tries: canUseDom() ? 1 : 2, // Retry once on the server, none on the browser
  // TODO REMOVE
  retryTimeout: 500, // Amount of time to wait between tries

  // The flag controls whether to try and match against a proxy route. It allows people to opt-out, even if they url
  // matches a valid proxy pattern
  useProxy: true,
};

async function patchedFetch(url, passedOptions = {}) {
  let req;
  let options = {};
  // NOTE: strictMode is a flag to avoid some forced stuff that happens
  // below in the code (where you can see more information about this).
  if (passedOptions.strictMode) {
    options = merge(options, strictDefaultOptions, passedOptions);
  } else {
    options = merge(options, defaultOptions, passedOptions);
  }

  // if passedOptions.req is provided, then get the token and add headers
  if (!canUseDom() && passedOptions.req) {
    req = passedOptions.req;

    let accessToken;
    try {
      // try to extract the token from the session object hydrated
      // by libAuth
      accessToken = req.tenxSession.getTenxAccessToken();
    } catch (err) {
      // if there is no user logged in and thus no session
      // then we use the appToken,
      // It is safe to use the app token at this stage since we are
      // in the backend making some service to service call (this
      // does not include the apiGateway)
      try {
        accessToken = await libAuth.getAppToken();
      } catch (err) {
        logger.error("error getting app token", err);
      }
    }

    options.headers = {
      authorization: `Bearer ${accessToken}`,
      ...req.commonHeaders,
      ...options.headers,
    };
  }

  const params = options.queryParams;
  const bodyToLog = options.body;
  const parseResponse = options.parseResponse;

  // if we are in the browser, then we need to proxy to certain routes
  if (canUseDom() && options.useProxy) {
    // We do not need to worry about queryParams here because they
    // are integrated a couples of lines below
    // We do not need to care about http methods right now because
    // if the client is calling a proxyRoute with the incorrect method
    // then the proxy hosted in the backend will reject it due to the way
    // it is configured. Check `apiGateway` for more information.
    try {
      const proxyRoute = createProxyRoute(url);
      url = proxyRoute.proxy.pathname;
    } catch (e) {
      /* will throw if the url is not proxy-able */
    }
  }

  if (canUseDom() || !options.headers["x-cid"]) {
    // This is is used to trace the request through the stack.
    // assign a new value if we are on the client, or the caller did not give us a value (usually from existing request)
    options.headers["x-cid"] = uuidv4();
  }

  url = constructUrl(url, params);

  // NOTE: Strip any options that you don't want passed through to the global fetch here.
  options = omit(options, ["queryParams", "req", "parseResponse", "useProxy"]);

  // NOTE: strictMode is a flag to avoid this following stringify and forced content-type
  // The reason behind this is that in some cases, we want to set a different
  // 'Content-Type' in the headers like for example when you need to use FormData
  // (as typeof FormData === 'object', Content-Type is forced to be application/json)
  // also, if you're using files, you might not want to do JSON.stringify()
  if (!options.strictMode) {
    // NOTE: If we serialize an object to JSON, we can force the correct content,
    // otherwise the caller should manually serialize the content.
    if (options.body && typeof options.body === "object") {
      options.body = JSON.stringify(options.body);
      options.headers["Content-Type"] = "application/json";
    }
  }

  //
  // XSRF protection
  //
  // TODO: Only add the header if we are making a request to an endpoint on the same domain as the
  // one this code is running under.
  //
  if (canUseDom()) {
    const xsrfCookieName = libAuth.getXsrfCookieName();
    const xsrfValue = Cookies.get(xsrfCookieName);

    if (xsrfValue) {
      options.headers[libAuth.XSRF_HEADER] = xsrfValue;
    }
  }

  var reqLog = new logger.RequestLogger();
  reqLog.request({
    uri: url,
    body: bodyToLog || {},
    headers: options.headers,
    xcid: options.headers["x-cid"] || null,
    userId: null,
    httpMethod: options.method,
  });

  let timesTried = 0;
  function doRequest() {
    timesTried++;
    return fetch(url, options)
      .catch(function (err) {
        /**
         * Retry on network errors
         * Catch before then so that we don't retry on throws from below caused by undesirable http codes
         */
        if (timesTried < options.tries) {
          ((req && req.logger) || logger).warn("Retrying request: " + url);
          return new Promise(function (resolve, reject) {
            setTimeout(function () {
              doRequest().then(resolve).catch(reject);
            }, options.retryTimeout);
          });
        }
        throw err;
      })
      .then(function (response) {
        if (response.ok) {
          return parseResponse(response);
        } else {
          return parseResponse(response).then(function (response) {
            if (response.body && typeof response.body === "object") {
              response.body.url = url;
              response.body.method = options.method;
            }

            throw response;
          });
        }
      })
      .then(function (parsedResponse) {
        const statusCode = parsedResponse.statusCode || 200;
        reqLog.response({
          statusCode,
          body: parsedResponse.body || {},
          headers: parsedResponse.headers || null,
        });

        return parsedResponse;
      })
      .catch(function (err) {
        reqLog.response({
          statusCode: err.status || 0,
          body: err.body || {},
          headers: err.headers || {},
        });

        throw err;
      });
  }

  return doRequest();
}

//
// WHATWG fetch does not support request streaming. It cannot be used for file uploads
// and other related request types. In those cases, we want to have the same API but change the
// underlying request to be a raw XMLHttpRequest without the caller knowing that.
//
// fetch.withProgress('url', {
//   method: 'POST',
//   isUpload: true,
//   body: {
//     file
//   },
//   onProgress: (perc) => {}
// }).then(console.log)
//   .catch(console.error)
//
const defaultProgressOptions = {
  method: "GET",
  isUpload: false,
  headers: {
    Accept: "application/json",
  },
  queryParams: {},
};

const getProgressErrorMessage = (responseText) => {
  let message;
  try {
    const parsedResponse =
      responseText.length > 0 ? JSON.parse(responseText) : {};
    message = get(parsedResponse, "message", "");
  } catch (parseError) {
    message = "";
  }
  return message;
};

const withProgress = (url, passedOptions) => {
  const options = merge({}, defaultProgressOptions, passedOptions);

  // if we are in the browser, then we need to proxy to certain routes
  if (!isNode) {
    try {
      const proxyRoute = createProxyRoute(url);
      url = proxyRoute.proxy.pathname;
    } catch (e) {
      /* will throw if url is invalid */
    }
    options.headers["x-cid"] = uuidv4();
  }

  //
  // XSRF protection
  //
  // TODO: Only add the header if we are making a request to an endpoint on the same domain as the
  // one this code is running under.
  //
  if (canUseDom()) {
    const xsrfCookieName = libAuth.getXsrfCookieName();
    const xsrfValue = Cookies.get(xsrfCookieName);

    if (xsrfValue) {
      options.headers[libAuth.XSRF_HEADER] = xsrfValue;
    }
  }

  return new Promise(function (resolve, reject) {
    const xhr = new window.XMLHttpRequest();
    let params = options.queryParams;

    url = constructUrl(url, params);

    xhr.open(options.method, url);
    xhr.responseType = options.responseType || xhr.responseType;

    /* All XHR Events */
    xhr.onload = function () {
      if (this.status >= 200 && this.status < 300) {
        const body =
          typeof xhr.response === "string"
            ? JSON.parse(xhr.response)
            : xhr.response;

        resolve({
          status: this.status,
          body,
        });
      } else {
        /* eslint-disable prefer-promise-reject-errors */
        reject({
          status: this.status,
          body: xhr.statusText,
          message: getProgressErrorMessage(xhr.responseText),
        });
      }
    };

    xhr.onerror = function () {
      /* eslint-disable prefer-promise-reject-errors */
      reject({
        status: this.status,
        body: xhr.statusText,
        message: getProgressErrorMessage(xhr.responseText),
      });
    };

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percentage = Math.round((e.loaded / e.total) * 100);
        if (options.onProgress) options.onProgress(percentage);
      }
    };

    if (options.headers) {
      Object.keys(options.headers).forEach(function (key) {
        xhr.setRequestHeader(key, options.headers[key]);
      });
    }

    // In the case where we have an upload, we'd like to use FormData
    //
    // NOTE: Skipping coverage for first if branch because we don't have Blob mocked in node yet.
    /* istanbul ignore if */
    if (typeof Blob !== "undefined" && options.body instanceof Blob) {
      params = options.body;
    } else if (options.isUpload) {
      params = new window.FormData();

      Object.keys(options.body).forEach((key) => {
        // In order for the Form object to know the key is a File, append must be given the filename
        // as the third argument
        if (key === "file" && typeof options.body[key] === "object") {
          params.append(key, options.body[key], options.body[key].name);
        } else {
          params.append(key, options.body[key]);
        }
      });
    }

    //
    // This is a little confusing because it can be a lot of things...
    //
    xhr.send(params);
  });
};

//
// NOTE: We use these util exports in lots of different places. Modifying them will
// potentially break apps in lots of little ways.
//
// import { helpers } from '@ten-x/core-utils'
//
// helpers.fetch(url String, opts Object)
// helpers.fetch.withProgress(url String, opts Object)
// helpers.fetch.serializeParams(params Object)
//
patchedFetch.withProgress = withProgress;
patchedFetch.responseParsers = responseParsers;
patchedFetch.serializeParams = serializeParams;

module.exports = patchedFetch;
